import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import OverageToggle from 'features/settings/components/panels/OverageToggle';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { TableFooter } from 'shared/components/tables';
import useBillingV2Status from 'shared/hooks/useBillingV2Status';
import {
  Section, KV, Pill, btnPrimary, btnNeutral, linkBtn,
  tableShell, th, thRow, trBody, td, MUTED,
} from 'features/settings/components/panels/askCreditsShared';

/**
 * Billing home for teams on a paid plan (pro / trial / enterprise). Free teams
 * get ProUpgradeCard instead, since there is nothing to invoice yet.
 */

const INVOICE_PAGE = 10;

const PLAN_LABELS = {
  free: 'Free',
  pro: 'Pro',
  trial: 'Pro trial',
  enterprise: 'Enterprise',
};

const QUIET = { color: 'var(--text-muted)', bg: 'transparent' };

/** Keys match BillingService.normalizeStripeStatus; 'trial' comes from the v2 entitlement. */
const STATUS_TONES = {
  active: { label: 'Active', color: 'var(--feedback-success)', bg: 'var(--feedback-success-bg)' },
  trial: { label: 'Trial', color: 'var(--feedback-warning)', bg: 'var(--feedback-warning-bg)' },
  past_due: { label: 'Past due', color: 'var(--feedback-error)', bg: 'var(--feedback-error-bg)' },
  cancelled: { label: 'Cancelled', ...QUIET },
  expired: { label: 'Expired', ...QUIET },
  incomplete: { label: 'Incomplete', color: 'var(--feedback-warning)', bg: 'var(--feedback-warning-bg)' },
};

function formatDate(value, opts = { year: 'numeric', month: 'short', day: 'numeric' }) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', opts);
}

/**
 * The period line under an invoice description, or null when there is nothing
 * honest to print.
 *
 * Three different periods all rendered as "Aug 5 – Aug 5" here, and only the
 * first was the one reported:
 *
 *   - Stripe's invoice-level window, which collapses to an instant on a first
 *     invoice. Fixed at the source — the backend now reads the line item's
 *     service period — and the `end > start` check below is the backstop.
 *   - A metered overage invoice whose real service window is SECONDS wide: the
 *     weekly overage subscription bills the moment it is armed, so both ends
 *     land on one calendar day. The span is real but tells the reader nothing,
 *     and the day is already in the Date column, so the line is dropped.
 *   - An annual invoice genuinely running Aug 5 2026 → Aug 5 2027. The start
 *     omitted its year, so a correct 365-day period still READ as a single day.
 *     This one is the ticket's own invoice: correct data, unreadable label.
 *     When the years differ, both ends now carry one.
 *
 * Formatting decides visibility, so the check has to live at formatting
 * granularity. Comparing timestamps is what let the last two through.
 */
function formatPeriod(period) {
  if (!period?.start || !period?.end) return null;
  const start = new Date(period.start);
  const end = new Date(period.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  if (end <= start) return null;
  // toDateString is local-timezone, matching toLocaleDateString below — the
  // test for "would these print the same" must use the reader's own calendar.
  if (start.toDateString() === end.toDateString()) return null;

  const startOpts = start.getFullYear() === end.getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { year: 'numeric', month: 'short', day: 'numeric' };
  return `${formatDate(period.start, startOpts)} – ${formatDate(period.end)}`;
}

function formatCurrency(amount, currency = 'USD') {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(amount));
  } catch {
    // Unknown currency code from Stripe — fall back to a plain number.
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
}

const INVOICE_TONES = {
  paid: { label: 'Paid', color: 'var(--feedback-success)', bg: 'var(--feedback-success-bg)' },
  open: { label: 'Due', color: 'var(--feedback-warning)', bg: 'var(--feedback-warning-bg)' },
  uncollectible: { label: 'Failed', color: 'var(--feedback-error)', bg: 'var(--feedback-error-bg)' },
  void: { label: 'Void', ...QUIET },
  draft: { label: 'Draft', ...QUIET },
};

function StatusPill({ status }) {
  const tone = STATUS_TONES[String(status || '').toLowerCase()];
  return tone ? <Pill {...tone} /> : null;
}

/** Unknown status codes still render, capitalised, rather than vanishing. */
function InvoiceStatusPill({ status }) {
  const key = String(status || '').toLowerCase();
  if (!key) return null;
  return <Pill {...(INVOICE_TONES[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), ...QUIET })} />;
}

const BillingOverview = ({
  teamId,
  plan,
  // Descriptive only — the latest subscription of ANY status. Used for the
  // heading when a payment has failed; never for access. See planLabel below.
  billingPlan,
  status,
  billingCycle,
  currentPeriodEnd,
  cancelAtPeriodEnd,
  amount,
  currency,
  growthQuota,
  hasStripeCustomer,
  onManageSubscription,
  manageLoading,
  onChangePlan,
  isTrial,
  trialEndsAt,
  onUpgradeNow,
  upgradeLoading,
  upgradeError,
  isEnterprise,
  contractEndsAt,
}) => {
  const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.trodo.ai';
  const { status: v2Billing } = useBillingV2Status(teamId);

  const [invoices, setInvoices] = useState(null);
  const [shown, setShown] = useState(INVOICE_PAGE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Ending a trial is irreversible and bills immediately, so it takes two
  // clicks. See the confirmation copy below for why (TRO-279).
  const [confirmingUpgrade, setConfirmingUpgrade] = useState(false);

  const loadInvoices = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/billing/history/${teamId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      // The server answers 200 with an empty list when Stripe itself failed;
      // `loadError` says so, so it is not shown as "no invoices".
      if (res.data?.data?.loadError) {
        setError('Could not load your invoices.');
        setInvoices([]);
        return;
      }
      setInvoices(res.data?.data?.invoices || res.data?.invoices || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load your invoices.');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [teamId, API_URL]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // On a trial `plan` reads 'pro' (Stripe reports the Pro price and the trial
  // status is normalised away), so the trial flag from the v2 entitlement is
  // what makes this honest.
  //
  // When a payment has failed, the heading names the plan they are ON, not the
  // access they currently have. `plan` is the live entitlement and reads 'free'
  // the moment a subscription stops being active — correct for gating, wrong as
  // a title, because it rendered "Current plan: Free" directly above the words
  // "Your Pro subscription is past due" (TRO-280). The page contradicted
  // itself, and the customer cannot act on a contradiction.
  //
  // LABEL ONLY. Access, limits and the pricing grid still derive from `plan` —
  // billingService is explicit that the descriptive value must never touch
  // effectivePlan / limits / features / isActive, and this does not.
  const NEEDS_PAYMENT = status === 'past_due' || status === 'unpaid' || status === 'incomplete';
  const headlinePlan = NEEDS_PAYMENT && billingPlan && billingPlan !== 'free' ? billingPlan : plan;
  const planLabel = isEnterprise
    ? PLAN_LABELS.enterprise
    : isTrial ? PLAN_LABELS.trial : (PLAN_LABELS[headlinePlan] || 'Plan');
  const statusValue = isEnterprise ? 'active' : isTrial ? 'trial' : status;

  /**
   * What ending the trial actually costs, said out loud.
   *
   * "Upgrade to Pro now" described the destination and hid the sacrifice:
   * end-trial sets trial_end to now with proration_behavior 'none', so the
   * remaining days are forfeited and the card is charged the same minute. Two
   * teams lost a freshly redeemed 14-day trial 11 seconds after redeeming it,
   * and landed past_due when that immediate charge failed (TRO-279).
   */
  const payNowLabel = useMemo(
    () => (amount ? formatCurrency(amount, currency) : 'the full amount'),
    [amount, currency],
  );

  const trialDaysLeft = useMemo(() => {
    if (!trialEndsAt) return null;
    const end = new Date(trialEndsAt).getTime();
    if (Number.isNaN(end)) return null;
    const days = Math.ceil((end - Date.now()) / 86400000);
    return days > 0 ? days : null;
  }, [trialEndsAt]);

  const renewalLabel = useMemo(() => {
    if (isEnterprise) return 'Contract ends';
    if (isTrial) return 'Trial ends';
    if (cancelAtPeriodEnd) return 'Access ends';
    return 'Next payment';
  }, [cancelAtPeriodEnd, isTrial, isEnterprise]);

  // The trial's own end date, not the subscription period — they differ, and
  // showing the period here would tell someone on a 14-day trial that nothing
  // happens for a year.
  const renewalDate = isEnterprise
    ? (contractEndsAt || currentPeriodEnd)
    : isTrial ? (trialEndsAt || currentPeriodEnd) : currentPeriodEnd;

  const cycleLabel =
    billingCycle === 'annual' ? 'Annual' : billingCycle === 'monthly' ? 'Monthly' : '—';

  // The entitlement in force is what the gate enforces — a trial has no
  // legacy quota and read "Custom". The legacy figure is only a fallback.
  const includedEvents = v2Billing?.entitlement?.units_included
    ?? growthQuota?.purchased_events ?? growthQuota?.purchasedEvents ?? null;

  return (
    <div className="w-full text-left" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* ---- Plan ---- */}
      <Section
        title="Plan"
        action={(
          <div className="flex flex-wrap items-center gap-2">
            {/* Ending a trial is irreversible and bills immediately, so it
                takes two clicks (TRO-279). */}
            {isTrial && onUpgradeNow && (
              confirmingUpgrade ? (
                <>
                  <button type="button" onClick={onUpgradeNow} disabled={upgradeLoading} className={btnPrimary}>
                    {upgradeLoading ? 'Starting Pro…' : `Pay ${payNowLabel} now`}
                  </button>
                  <button type="button" onClick={() => setConfirmingUpgrade(false)} disabled={upgradeLoading} className={btnNeutral}>
                    Cancel
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => setConfirmingUpgrade(true)} className={btnPrimary}>
                  Upgrade to Pro
                </button>
              )
            )}
            {onChangePlan && plan !== 'enterprise' && !isTrial && (
              <button type="button" onClick={onChangePlan} className={btnNeutral}>Change plan</button>
            )}
            {/* Not for enterprise: the Stripe portal offers cancel and plan
                changes, and their terms are a signed contract. */}
            {hasStripeCustomer && !isEnterprise && (
              <button
                type="button"
                onClick={onManageSubscription}
                disabled={manageLoading}
                className={isTrial && onUpgradeNow ? btnNeutral : btnPrimary}
              >
                {manageLoading ? 'Opening…' : 'Manage billing'}
              </button>
            )}
          </div>
        )}
      >
        <KV label="Plan">
          <span className="inline-flex items-center gap-2">
            {planLabel}
            <StatusPill status={statusValue} />
          </span>
        </KV>
        {!isEnterprise && <KV label="Billing cycle">{cycleLabel}</KV>}
        <KV label={renewalLabel}>{formatDate(renewalDate)}</KV>
        {!isEnterprise && (
          <KV label={isTrial ? (cycleLabel === 'Annual' ? 'Then / year' : 'Then / month') : (cycleLabel === 'Annual' ? 'Amount / year' : 'Amount / month')}>
            {amount ? formatCurrency(amount, currency) : plan === 'enterprise' ? 'Custom' : '—'}
          </KV>
        )}
        {!isEnterprise && (
          <KV label="Included traces">
            {includedEvents != null ? Number(includedEvents).toLocaleString() : 'Custom'}
          </KV>
        )}
        <OverageToggle teamId={teamId} onManageSubscription={onManageSubscription} />

        {confirmingUpgrade && (
          <p className="mt-3 text-[12px]" style={{ color: 'var(--feedback-warning)' }}>
            Ends your trial and charges {payNowLabel} now
            {trialDaysLeft ? `, giving up ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'}` : ''}. This can&apos;t be undone.
          </p>
        )}
        {cancelAtPeriodEnd && !isTrial && !isEnterprise && (
          <p className="mt-3 text-[12px]" style={{ color: MUTED }}>
            Cancelled. Active until {formatDate(currentPeriodEnd)}.
          </p>
        )}
        {upgradeError && (
          <p className="mt-3 text-[12px]" style={{ color: 'var(--feedback-error)' }}>{upgradeError}</p>
        )}
      </Section>

      {/* ---- Invoices ---- */}
      <Section title="Invoices">
        {loading && !invoices ? (
          <BrandLoader size="md" label="Loading invoices" />
        ) : error ? (
          <div className="flex items-center gap-3 text-[12px]" style={{ color: MUTED }}>
            {error}
            <button type="button" onClick={loadInvoices} className={linkBtn}>Retry</button>
          </div>
        ) : !invoices?.length ? (
          <div className="text-[12px]" style={{ color: MUTED }}>No invoices yet.</div>
        ) : (
          <>
            <div className={tableShell}>
              <table className="w-full min-w-[640px] border-collapse">
                <thead>
                  <tr className={thRow}>
                    <th className={th} style={{ width: 120 }}>Date</th>
                    <th className={th} style={{ width: 150 }}>Number</th>
                    <th className={th}>Description</th>
                    <th className={`${th} text-right`} style={{ width: 100 }}>Amount</th>
                    <th className={th} style={{ width: 90 }}>Status</th>
                    <th className={th} style={{ width: 110 }}>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.slice(0, shown).map((inv) => {
                    const periodLabel = formatPeriod(inv.period);
                    return (
                      <tr key={inv.id} className={trBody} style={periodLabel ? { height: 44 } : undefined}>
                        <td className={`${td} tabular-nums`}>{formatDate(inv.date)}</td>
                        <td className={`${td} tabular-nums`} style={{ color: MUTED }}>{inv.number || '—'}</td>
                        <td className={td}>
                          <div className="truncate">{inv.description || inv.planName || 'Subscription'}</div>
                          {periodLabel && (
                            <div className="truncate text-[11px]" style={{ color: MUTED }}>{periodLabel}</div>
                          )}
                        </td>
                        <td className={`${td} text-right tabular-nums`}>{formatCurrency(inv.amount, inv.currency)}</td>
                        <td className={td}><InvoiceStatusPill status={inv.status} /></td>
                        <td className={td}>
                          <span className="inline-flex items-center gap-3">
                            {inv.downloadUrl && (
                              <a
                                href={inv.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Download invoice ${inv.number || ''} as PDF`}
                                className={linkBtn}
                              >
                                PDF
                              </a>
                            )}
                            {inv.viewUrl && (
                              <a
                                href={inv.viewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`View invoice ${inv.number || ''}`}
                                className={linkBtn}
                              >
                                View
                              </a>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableFooter
              shown={Math.min(shown, invoices.length)}
              total={invoices.length}
              hasMore={shown < invoices.length}
              onLoadMore={() => setShown((n) => n + INVOICE_PAGE)}
            />
          </>
        )}
      </Section>
    </div>
  );
};

// Exported for the regression test: the period shapes it rejects or re-labels
// were each found in live Stripe data.
export { formatPeriod };

export default BillingOverview;
